/**
 * Live chat runtime: one store per thread holding the in-flight run (optimistic user messages, the
 * streaming assistant draft, tool calls, interjections). It lives at module level so a run keeps
 * streaming while the user navigates (/chat → /chat/:id, the Ask drawer → full page).
 *
 * Rendering contract: while a thread has live items, show persisted messages `[0, baseCount)` followed by
 * `items`; when the run ends we refetch the thread and drop the live tail (persisted rows take over).
 */
import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { errorMessage } from "@/api/client";
import {
  answersPath,
  chatRequest,
  createThread,
  eventsPath,
  markThreadRead,
  messagesPath,
  normalizeMessage,
  stopThread,
  type AnswerItem,
  type ChatAttachment,
  type ChatMessage,
  type ChatThread,
  type CreateThreadBody,
  type SendMessageBody,
  type StreamEvent,
} from "@/api/chat";
import { threadKey, threadsKey } from "@/api/chat-hooks";
import { toast } from "@/api/toast-store";
import { lastBrand } from "@/state/brand";
import { pickObject } from "@/api/types";

export interface ToolSeg {
  kind: "tool";
  id: string;
  name: string;
  input?: unknown;
}
export type DraftSeg = { kind: "text"; text: string } | ToolSeg | { kind: "attachment"; attachment: ChatAttachment };

export interface LocalUserMsg {
  localId: string;
  text: string;
  createdAt: string;
  attachments: ChatAttachment[];
  state: "sending" | "sent" | "queued" | "applied" | "failed";
  interjection: boolean;
  serverId?: string;
  error?: string;
  /** How to resend on Retry. */
  retry?: { path: string; body: unknown };
}

export type LiveItem =
  | { kind: "user"; msg: LocalUserMsg }
  | { kind: "message"; message: ChatMessage }
  | { kind: "draft"; id: string; segs: DraftSeg[]; startedAt: string };

export interface ToolOutcome {
  result?: unknown;
  error?: string;
}

export interface ThreadLive {
  /** A run stream is open in this tab. */
  streaming: boolean;
  /** Persisted messages shown before the live tail; null = show all persisted. */
  baseCount: number | null;
  items: LiveItem[];
  toolResults: Record<string, ToolOutcome>;
  phase: "idle" | "thinking" | "responding" | "tool";
  status?: string;
  /** Server status label ("Mimic is thinking…", "Analyzing image…"); cleared when text streams. */
  statusLabel?: string | null;
  error?: string | null;
  notice?: "stopped" | null;
  stopping?: boolean;
  contextTokens?: number;
  contextWindow?: number;
  runToken: number;
}

const EMPTY: ThreadLive = Object.freeze({ streaming: false, baseCount: null, items: [], toolResults: {}, phase: "idle", runToken: 0 }) as ThreadLive;

const store = new Map<string, ThreadLive>();
const storeBrands = new Map<string, string | null>();
const controllers = new Map<string, AbortController>();
const listeners = new Set<() => void>();
let selectionEpoch = 0;
// Brand equality alone cannot detect A → B → A while a request is in flight.
function selectionGuard(): () => boolean {
  const epoch = selectionEpoch;
  const brand = lastBrand();
  return () => epoch === selectionEpoch && brand === lastBrand();
}
let tokenSeq = 0;
let localSeq = 0;

function emit() {
  for (const l of listeners) l();
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getLive(threadId: string | null | undefined): ThreadLive {
  return (threadId && storeBrands.get(threadId) === lastBrand() && store.get(threadId)) || EMPTY;
}
function update(threadId: string, fn: (l: ThreadLive) => ThreadLive) {
  const next = fn(getLive(threadId));
  store.set(threadId, next);
  storeBrands.set(threadId, lastBrand());
  emit();
}

export function useLive(threadId: string | null | undefined): ThreadLive {
  return useSyncExternalStore(
    subscribe,
    () => getLive(threadId),
    () => EMPTY,
  );
}

const nowIso = () => new Date().toISOString();
const newLocalId = () => `local-${Date.now().toString(36)}-${++localSeq}`;

// ─── Event reducer ─────────────────────────────────────────────────────────

function lastDraft(items: LiveItem[]): { index: number; item: Extract<LiveItem, { kind: "draft" }> } | null {
  const last = items[items.length - 1];
  return last && last.kind === "draft" ? { index: items.length - 1, item: last } : null;
}

function pushSeg(items: LiveItem[], seg: DraftSeg): LiveItem[] {
  const d = lastDraft(items);
  if (d) {
    const segs = [...d.item.segs];
    const tail = segs[segs.length - 1];
    if (seg.kind === "text" && tail?.kind === "text") segs[segs.length - 1] = { kind: "text", text: tail.text + seg.text };
    else segs.push(seg);
    const next = [...items];
    next[d.index] = { ...d.item, segs };
    return next;
  }
  return [...items, { kind: "draft", id: newLocalId(), segs: [seg], startedAt: nowIso() }];
}

function setUser(items: LiveItem[], pred: (m: LocalUserMsg) => boolean, patch: (m: LocalUserMsg) => Partial<LocalUserMsg>, onlyFirst = true): LiveItem[] {
  let done = false;
  return items.map((i) => {
    if (i.kind !== "user" || (onlyFirst && done) || !pred(i.msg)) return i;
    done = true;
    return { kind: "user", msg: { ...i.msg, ...patch(i.msg) } };
  });
}

const NOT_USER_TEXT = /^\s*(<context>[\s\S]*<\/context>|\[Attached (image|video):[\s\S]*\])\s*$/;

function textOf(m: ChatMessage): string {
  return m.content
    .filter((b) => b.type === "text")
    .map((b) => String((b as { text?: unknown }).text ?? ""))
    .filter((t) => t.trim() && !NOT_USER_TEXT.test(t))
    .join("\n\n")
    .trim();
}

export function reduceLive(l: ThreadLive, e: StreamEvent): ThreadLive {
  switch (e.type) {
    case "text":
      if (!e.delta) return l;
      return { ...l, phase: "responding", statusLabel: null, items: pushSeg(l.items, { kind: "text", text: e.delta }) };
    case "thinking":
      return l.phase === "thinking" ? l : { ...l, phase: "thinking" };
    case "status":
      return { ...l, status: e.status, statusLabel: e.label ?? l.statusLabel, phase: e.label && l.phase === "responding" ? "thinking" : l.phase };
    case "tool_use":
      return { ...l, phase: "tool", statusLabel: null, items: pushSeg(l.items, { kind: "tool", id: e.id || newLocalId(), name: e.name, input: e.input }) };
    case "tool_result":
      return { ...l, phase: "thinking", toolResults: { ...l.toolResults, [e.id]: { result: e.result, error: e.error } } };
    case "attachment":
      return { ...l, items: pushSeg(l.items, { kind: "attachment", attachment: e.attachment }) };
    case "question":
      return { ...l, status: "awaiting_user", phase: "idle", items: pushSeg(l.items, { kind: "attachment", attachment: { ...e.attachment, kind: "question" } }) };
    case "message": {
      const m = e.message;
      if (l.items.some((i) => i.kind === "message" && i.message.id === m.id)) return l;
      if (m.role === "assistant") {
        const d = lastDraft(l.items);
        if (d) {
          const next = [...l.items];
          next[d.index] = { kind: "message", message: m };
          return { ...l, items: next };
        }
        return { ...l, items: [...l.items, { kind: "message", message: m }] };
      }
      if (m.role === "user") {
        const text = textOf(m);
        const match = l.items.find((i) => i.kind === "user" && !i.msg.serverId && i.msg.text.trim() === text && i.msg.state !== "failed");
        if (match && match.kind === "user") {
          return { ...l, items: setUser(l.items, (u) => u.localId === match.msg.localId, (u) => ({ serverId: m.id, state: u.interjection ? "applied" : "sent" })) };
        }
      }
      return { ...l, items: [...l.items, { kind: "message", message: m }] };
    }
    case "usage": {
      const ctx = e.contextTokens ?? e.inputTokens;
      return { ...l, contextTokens: ctx ?? l.contextTokens, contextWindow: e.contextWindow ?? l.contextWindow };
    }
    case "interjection_queued": {
      const items = setUser(
        l.items,
        (u) => u.interjection && !u.serverId && (u.state === "sending" || u.state === "sent") && (!e.text || u.text.trim() === e.text.trim()),
        () => ({ state: "queued", serverId: e.id }),
      );
      return { ...l, items };
    }
    case "interjection_applied": {
      const ids = new Set(e.ids);
      const anyMatch = l.items.some((i) => i.kind === "user" && i.msg.serverId && ids.has(i.msg.serverId));
      const items = setUser(
        l.items,
        (u) => u.interjection && u.state !== "failed" && u.state !== "applied" && (anyMatch ? !!u.serverId && ids.has(u.serverId) : true),
        () => ({ state: "applied" }),
        false,
      );
      return { ...l, items, phase: "thinking" };
    }
    case "stopped":
      return { ...l, phase: "idle", notice: "stopped", stopping: false };
    case "done":
      return { ...l, phase: "idle" };
    case "error":
      return { ...l, error: e.message };
    default:
      return l;
  }
}

// ─── Runs ──────────────────────────────────────────────────────────────────

/** Forget local transcripts/streams on selection changes; the server run can finish safely in its own brand. */
export function clearChatSelection(qc: QueryClient): void {
  selectionEpoch++;
  for (const ac of controllers.values()) ac.abort();
  controllers.clear();
  store.clear();
  storeBrands.clear();
  void qc.cancelQueries({ queryKey: ["chat"] });
  qc.removeQueries({ queryKey: ["chat", "thread"] });
  qc.removeQueries({ queryKey: threadsKey });
  emit();
}

async function reconcile(qc: QueryClient, threadId: string, token: number) {
  const isCurrent = selectionGuard();
  if (getLive(threadId).runToken !== token) return;
  void qc.invalidateQueries({ queryKey: threadsKey });
  try {
    await qc.refetchQueries({ queryKey: threadKey(threadId), exact: true });
  } catch {
    /* error state is shown by the query */
  }
  if (!isCurrent() || getLive(threadId).runToken !== token) return;
  const persisted = qc.getQueryData<{ messages: ChatMessage[] }>(threadKey(threadId))?.messages ?? [];
  update(threadId, (l) => {
    if (l.runToken !== token || l.streaming) return l;
    const keep = l.items.filter((i) => {
      if (i.kind !== "user") return false;
      if (i.msg.state === "failed") return true;
      if (i.msg.state === "queued" || i.msg.state === "sending") {
        const since = Date.parse(i.msg.createdAt) - 5000;
        return !persisted.some((m) => m.role === "user" && textOf(m) === i.msg.text.trim() && (Date.parse(m.createdAt ?? "") || 0) >= since);
      }
      return false;
    });
    return { ...l, items: keep, baseCount: null, toolResults: {}, phase: "idle" };
  });
  void markThreadRead(threadId).then(() => {
    if (isCurrent()) void qc.invalidateQueries({ queryKey: threadsKey });
  });
}

async function runStream(qc: QueryClient, threadId: string, path: string, body: unknown, localId: string | null, method: "GET" | "POST" = "POST"): Promise<{ ok: boolean; error?: unknown }> {
  const isCurrent = selectionGuard();
  const token = ++tokenSeq;
  controllers.get(threadId)?.abort();
  const ac = new AbortController();
  controllers.set(threadId, ac);
  update(threadId, (l) => ({ ...l, streaming: true, phase: "thinking", statusLabel: null, error: null, notice: null, stopping: false, status: "running", runToken: token }));
  let gotEvent = false;
  let outcome: { ok: boolean; error?: unknown } = { ok: true };
  const apply = (e: StreamEvent) => {
    if (getLive(threadId).runToken !== token) return;
    if (!gotEvent && localId) update(threadId, (l) => ({ ...l, items: setUser(l.items, (u) => u.localId === localId, (u) => ({ state: u.state === "sending" ? "sent" : u.state })) }));
    gotEvent = true;
    update(threadId, (l) => reduceLive(l, e));
  };
  try {
    const r = await chatRequest(path, body, { signal: ac.signal, onEvent: apply, method });
    if (!isCurrent() || getLive(threadId).runToken !== token) return { ok: false };
    if (!r.streamed && r.json && typeof r.json === "object") {
      const j = r.json as Record<string, unknown>;
      const m = pickObject<ChatMessage>(j, "message");
      if (m && Array.isArray((m as { content?: unknown }).content) && m.role) apply({ type: "message", message: normalizeMessage(m) });
      const queued = j.queued === true || j.status === "queued" || !!j.interjection;
      const inputId = typeof j.inputId === "string" ? j.inputId : undefined;
      if (localId) {
        update(threadId, (l) => ({
          ...l,
          items: setUser(l.items, (u) => u.localId === localId, (u) => ({ state: queued ? "queued" : u.state === "sending" ? "sent" : u.state, interjection: u.interjection || queued, serverId: u.serverId ?? inputId })),
        }));
      }
    } else if (localId) {
      update(threadId, (l) => ({ ...l, items: setUser(l.items, (u) => u.localId === localId, (u) => ({ state: u.state === "sending" ? "sent" : u.state })) }));
    }
  } catch (err) {
    if (!isCurrent() || getLive(threadId).runToken !== token) return { ok: false };
    outcome = { ok: false, error: err };
    if (localId && !gotEvent) {
      update(threadId, (l) => ({ ...l, items: setUser(l.items, (u) => u.localId === localId, () => ({ state: "failed", error: errorMessage(err) })) }));
    } else {
      update(threadId, (l) => (l.runToken === token ? { ...l, error: errorMessage(err) } : l));
    }
  } finally {
    if (controllers.get(threadId) === ac) controllers.delete(threadId);
    if (isCurrent() && getLive(threadId).runToken === token) update(threadId, (l) => ({ ...l, streaming: false, stopping: false, phase: "idle" }));
  }
  await reconcile(qc, threadId, token);
  return outcome;
}

function persistedCount(qc: QueryClient, threadId: string): number {
  return qc.getQueryData<{ messages: ChatMessage[] }>(threadKey(threadId))?.messages.length ?? 0;
}

/**
 * Send a user message. While this tab holds the run's stream it's an interjection (queued server-side
 * and applied at the next turn boundary); otherwise it starts (or attaches to) a run.
 */
export async function sendMessage(qc: QueryClient, threadId: string, body: SendMessageBody, opts: { display?: ChatAttachment[] } = {}): Promise<void> {
  const isCurrent = selectionGuard();
  const live = getLive(threadId);
  const local: LocalUserMsg = {
    localId: newLocalId(),
    text: body.content,
    createdAt: nowIso(),
    attachments: opts.display ?? [],
    state: "sending",
    interjection: live.streaming,
    retry: { path: messagesPath(threadId), body },
  };
  if (live.streaming) {
    update(threadId, (l) => ({ ...l, items: [...l.items, { kind: "user", msg: local }] }));
    try {
      const r = await chatRequest(messagesPath(threadId), body, { consume: false });
      if (!isCurrent()) return;
      const j = (r.json && typeof r.json === "object" ? r.json : {}) as Record<string, unknown>;
      const inputId = typeof j.inputId === "string" ? j.inputId : typeof pickObject<{ id?: string }>(j, "input")?.id === "string" ? pickObject<{ id: string }>(j, "input")!.id : undefined;
      update(threadId, (l) => ({
        ...l,
        items: setUser(l.items, (u) => u.localId === local.localId, (u) => ({ state: u.state === "applied" ? "applied" : "queued", serverId: u.serverId ?? inputId })),
      }));
    } catch (err) {
      if (!isCurrent()) return;
      update(threadId, (l) => ({ ...l, items: setUser(l.items, (u) => u.localId === local.localId, () => ({ state: "failed", error: errorMessage(err) })) }));
    }
    return;
  }
  const base = persistedCount(qc, threadId);
  update(threadId, (l) => ({ ...l, baseCount: l.baseCount ?? base, items: [...l.items, { kind: "user", msg: local }], error: null, notice: null }));
  await runStream(qc, threadId, messagesPath(threadId), body, local.localId);
}

/** Resend a failed message (or answer). */
export async function retryLocal(qc: QueryClient, threadId: string, localId: string): Promise<void> {
  const isCurrent = selectionGuard();
  const item = getLive(threadId).items.find((i) => i.kind === "user" && i.msg.localId === localId);
  if (!item || item.kind !== "user" || !item.msg.retry) return;
  const { path, body } = item.msg.retry;
  update(threadId, (l) => ({ ...l, items: setUser(l.items, (u) => u.localId === localId, () => ({ state: "sending", error: undefined })) }));
  if (getLive(threadId).streaming) return void sendMessage(qc, threadId, body as SendMessageBody, { display: item.msg.attachments }).then(() => { if (isCurrent()) discardLocal(threadId, localId); });
  const base = persistedCount(qc, threadId);
  update(threadId, (l) => ({ ...l, baseCount: l.baseCount ?? base, error: null }));
  await runStream(qc, threadId, path, body, localId);
}

export function discardLocal(threadId: string, localId: string): void {
  update(threadId, (l) => ({ ...l, items: l.items.filter((i) => !(i.kind === "user" && i.msg.localId === localId)) }));
}

export function clearError(threadId: string): void {
  update(threadId, (l) => ({ ...l, error: null }));
}

/** Submit (or dismiss) an ask_user question card; continues the run. Resolves false on failure. */
export async function answerQuestion(
  qc: QueryClient,
  threadId: string,
  attachmentId: string,
  payload: { answers: AnswerItem[]; cancelled?: boolean; summary: Record<string, unknown> },
): Promise<boolean> {
  const isCurrent = selectionGuard();
  const body = { attachmentId, answers: payload.answers, ...(payload.cancelled ? { cancelled: true } : {}) };
  const local: LocalUserMsg = {
    localId: newLocalId(),
    text: "",
    createdAt: nowIso(),
    attachments: [{ kind: "answers", id: attachmentId, data: payload.summary }],
    state: "sending",
    interjection: false,
    retry: { path: answersPath(threadId), body },
  };
  const base = persistedCount(qc, threadId);
  update(threadId, (l) => ({ ...l, baseCount: l.baseCount ?? base, items: [...l.items, { kind: "user", msg: local }], error: null, notice: null }));
  const r = await runStream(qc, threadId, answersPath(threadId), body, local.localId);
  if (!isCurrent()) return false;
  if (!r.ok) {
    discardLocal(threadId, local.localId);
    toast({ tone: "danger", title: "Couldn't send your answer", body: errorMessage(r.error) });
  }
  return r.ok;
}

/** Ask the server to stop the run (applied at the next turn boundary). */
export async function stopRun(qc: QueryClient, threadId: string): Promise<void> {
  const isCurrent = selectionGuard();
  const token = getLive(threadId).runToken;
  const ownsRun = () => isCurrent() && getLive(threadId).runToken === token;
  update(threadId, (l) => ({ ...l, stopping: true }));
  try {
    await stopThread(threadId);
  } catch (err) {
    if (!ownsRun()) return;
    toast({ tone: "danger", title: "Couldn't stop the run", body: errorMessage(err) });
    // The server is unreachable: drop our stream so the UI isn't stuck.
    controllers.get(threadId)?.abort();
  }
  if (!ownsRun()) return;
  const ac = controllers.get(threadId);
  if (ac) {
    window.setTimeout(() => {
      if (ownsRun() && controllers.get(threadId) === ac && getLive(threadId).stopping) ac.abort();
    }, 10_000);
  } else {
    update(threadId, (l) => ({ ...l, stopping: false, notice: "stopped" }));
    void qc.invalidateQueries({ queryKey: threadKey(threadId) });
  }
}

/** Create a thread, seed the cache, hand the id to the caller (navigation), then stream the first message. */
export async function createAndSend(
  qc: QueryClient,
  create: CreateThreadBody,
  body: SendMessageBody,
  opts: { display?: ChatAttachment[]; onCreated?: (thread: ChatThread) => void } = {},
): Promise<string> {
  const isCurrent = selectionGuard();
  const thread = await createThread(create);
  if (!isCurrent()) return thread.id;
  // Seed the cache so the thread page renders instantly (and doesn't refetch over the live tail).
  // A context ("Ask about this") message is created server-side; it's picked up when the run reconciles.
  qc.setQueryData(threadKey(thread.id), { thread: { ...thread, status: "running" }, messages: [] });
  void qc.invalidateQueries({ queryKey: threadsKey });
  opts.onCreated?.(thread);
  void sendMessage(qc, thread.id, body, { display: opts.display });
  return thread.id;
}

/** Re-attach to a run that's live on the server (reload / navigated away and back). */
export async function reattach(qc: QueryClient, threadId: string): Promise<void> {
  if (getLive(threadId).streaming) return;
  const base = persistedCount(qc, threadId);
  update(threadId, (l) => ({ ...l, baseCount: l.baseCount ?? base }));
  await runStream(qc, threadId, eventsPath(threadId), undefined, null, "GET");
}

export function isStreaming(threadId: string | null | undefined): boolean {
  return getLive(threadId).streaming;
}
